import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, KeyRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, isStaff } from "@/lib/auth";
import { api, errMsg, money, fdate, MODE_LABEL } from "@/lib/api";
import { useSeo } from "@/lib/analytics";
import { Field, NativeSelect, COUNTRIES } from "./Forms";

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return <button type="button" onClick={go} className="btn-ghost-cc w-full border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="google-login-btn">Continuar con Google</button>;
}

export function LoginPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const [f, setF] = useState({ email: "", password: "", name: "", company: "", country: "Costa Rica", mfa_code: "" });
  const [mfa, setMfa] = useState(false);
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Acceso de clientes" });
  const go = (u) => navigate(next || (isStaff(u) ? "/admin" : "/portal"), { replace: true });
  useEffect(() => { if (user) go(user); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      go(await login(f.email, f.password, f.mfa_code));
    } catch (err) {
      const m = errMsg(err);
      if (m === "mfa_required") { setMfa(true); toast.info("Ingrese el código de su app autenticadora"); } else toast.error(m);
    } finally { setBusy(false); }
  };
  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      go(await register({ email: f.email, password: f.password, name: f.name, company: f.company, country: f.country }));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <section className="wrap max-w-md py-20">
      <h1 className="text-3xl font-bold">Portal de clientes</h1>
      <p className="mt-2 text-slate-500">Gestione sus productos, licencias, suscripciones y facturas.</p>
      <Tabs defaultValue="login" className="mt-8">
        <TabsList className="grid w-full grid-cols-2"><TabsTrigger value="login" data-testid="tab-login">Iniciar sesión</TabsTrigger><TabsTrigger value="register" data-testid="tab-register">Crear cuenta</TabsTrigger></TabsList>
        <TabsContent value="login">
          <form onSubmit={doLogin} className="card-cc mt-4 space-y-4" data-testid="login-form">
            <Field label="Correo"><Input type="email" required value={f.email} onChange={set("email")} data-testid="login-email" /></Field>
            <Field label="Contraseña"><Input type="password" required value={f.password} onChange={set("password")} data-testid="login-password" /></Field>
            {mfa && <Field label="Código MFA (6 dígitos)"><Input inputMode="numeric" value={f.mfa_code} onChange={set("mfa_code")} data-testid="login-mfa-code" /></Field>}
            <button disabled={busy} className="btn-cc w-full" data-testid="login-submit">{busy ? "Ingresando…" : "Ingresar"}</button>
            <GoogleButton />
          </form>
        </TabsContent>
        <TabsContent value="register">
          <form onSubmit={doRegister} className="card-cc mt-4 space-y-4" data-testid="register-form">
            <Field label="Nombre"><Input required value={f.name} onChange={set("name")} data-testid="register-name" /></Field>
            <Field label="Empresa"><Input value={f.company} onChange={set("company")} data-testid="register-company" /></Field>
            <Field label="País"><NativeSelect value={f.country} onChange={set("country")} data-testid="register-country">{COUNTRIES.map((c) => <option key={c}>{c}</option>)}</NativeSelect></Field>
            <Field label="Correo"><Input type="email" required value={f.email} onChange={set("email")} data-testid="register-email" /></Field>
            <Field label="Contraseña (mín. 8 caracteres)"><Input type="password" minLength={8} required value={f.password} onChange={set("password")} data-testid="register-password" /></Field>
            <button disabled={busy} className="btn-cc w-full" data-testid="register-submit">Crear cuenta</button>
            <GoogleButton />
          </form>
        </TabsContent>
      </Tabs>
    </section>
  );
}

const TONE = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200", blue: "bg-blue-50 text-blue-700 border-blue-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200", red: "bg-red-50 text-red-700 border-red-200", slate: "bg-slate-50 text-slate-600 border-slate-200",
};
const STATUS_TONE = { active: "green", paid: "green", approved: "green", published: "green", trialing: "blue", pending_payment: "amber", initiated: "amber", nuevo: "amber",
  pendiente_confirmacion: "amber", draft: "slate", pending: "amber", payment_failed: "red", failed: "red", refunded: "slate", revoked: "red", cancelled: "slate" };
const STATUS_ES = { active: "Activo", paid: "Pagado", approved: "Aprobado", published: "Publicado", trialing: "En prueba", pending_payment: "Pago pendiente", initiated: "Iniciado",
  nuevo: "Nuevo", pendiente_confirmacion: "Pendiente de confirmación", draft: "Borrador", pending: "Pendiente", payment_failed: "Pago rechazado", failed: "Fallido",
  refunded: "Reembolsado", revoked: "Revocada", cancelled: "Cancelado", en_seguimiento: "En seguimiento", confirmada: "Confirmada", cerrado: "Cerrado" };
export const Badge = ({ s }) => <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${TONE[STATUS_TONE[s] || "slate"]}`}>{STATUS_ES[s] || String(s ?? "—")}</span>;

const Empty = ({ text }) => <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">{text}</p>;

function Profile({ p, reload }) {
  const [f, setF] = useState(p);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/portal/profile", { name: f.name, company: f.company, country: f.country, tax_id: f.tax_id, phone: f.phone, address: f.address }); toast.success("Perfil actualizado"); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="card-cc grid gap-4 sm:grid-cols-2" data-testid="profile-form">
      <Field label="Nombre"><Input value={f.name || ""} onChange={set("name")} data-testid="profile-name" /></Field>
      <Field label="Correo"><Input value={f.email} disabled /></Field>
      <Field label="Empresa"><Input value={f.company || ""} onChange={set("company")} data-testid="profile-company" /></Field>
      <Field label="País"><Input value={f.country || ""} onChange={set("country")} /></Field>
      <Field label="Identificación tributaria"><Input value={f.tax_id || ""} onChange={set("tax_id")} data-testid="profile-tax-id" /></Field>
      <Field label="Teléfono"><Input value={f.phone || ""} onChange={set("phone")} /></Field>
      <div className="sm:col-span-2"><Field label="Dirección"><Input value={f.address || ""} onChange={set("address")} /></Field></div>
      <button className="btn-cc sm:col-span-2 sm:w-fit" data-testid="profile-save">Guardar perfil</button>
    </form>
  );
}

function Support() {
  const [f, setF] = useState({ subject: "", message: "", product: "" });
  const send = async (e) => {
    e.preventDefault();
    try { await api.post("/portal/support", f); toast.success("Solicitud de soporte enviada"); setF({ subject: "", message: "", product: "" }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={send} className="card-cc space-y-4" data-testid="support-form">
      <Field label="Asunto"><Input required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="support-subject" /></Field>
      <Field label="Producto"><Input value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} data-testid="support-product" /></Field>
      <Field label="Mensaje (mín. 10 caracteres)"><Textarea rows={5} required value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="support-message" /></Field>
      <button className="btn-cc" data-testid="support-submit">Enviar</button>
    </form>
  );
}

export function PortalPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = () => api.get("/portal/summary").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useSeo({ title: "Mi portal" });
  const cancel = async (id) => {
    if (!window.confirm("¿Solicitar la cancelación al final del período?")) return;
    try { await api.post(`/portal/subscriptions/${id}/cancel`); toast.success("Cancelación programada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <div className="wrap py-32 text-slate-500">Cargando portal…</div>;
  return (
    <section className="wrap py-14" data-testid="customer-portal">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><p className="eyebrow">Portal de clientes</p><h1 className="mt-2 text-3xl font-bold">Hola, {d.profile.name}</h1></div>
        <div className="flex gap-2">
          {isStaff(d.profile) && <Link to="/admin" className="btn-ghost-cc border-slate-300" data-testid="portal-admin-link">Panel admin</Link>}
          <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost-cc border-slate-300" data-testid="portal-logout"><LogOut className="h-4 w-4" /> Salir</button>
        </div>
      </div>
      <Tabs defaultValue="products" className="mt-8">
        <TabsList className="flex h-auto flex-wrap justify-start">
          {[["products", "Productos y licencias"], ["subs", "Suscripciones"], ["invoices", "Facturas y pagos"], ["requests", "Solicitudes"], ["downloads", "Descargas"], ["support", "Soporte"], ["profile", "Perfil empresarial"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`portal-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="products" className="mt-6">
          {d.licenses.length ? <div className="grid gap-4 md:grid-cols-2">{d.licenses.map((l) => (
            <div key={l.id} className="card-cc" data-testid={`license-${l.id}`}>
              <div className="flex items-center justify-between"><p className="font-display font-semibold">{l.product_name} · {l.plan_name}</p><Badge s={l.status} /></div>
              <p className="mt-3 flex items-center gap-2 font-mono text-sm text-slate-700"><KeyRound className="h-4 w-4 text-brand" /><span data-testid="license-key">{l.key}</span></p>
              <p className="mt-2 text-xs text-slate-500">Puestos: {l.seats} · Vence: {l.expires_at ? fdate(l.expires_at) : "Perpetua"}</p>
            </div>))}</div> : <Empty text="Aún no tiene productos contratados." />}
        </TabsContent>
        <TabsContent value="subs" className="mt-6 space-y-3">
          {d.subscriptions.length ? d.subscriptions.map((s) => (
            <div key={s.id} className="card-cc flex flex-wrap items-center justify-between gap-4 !p-5" data-testid={`subscription-${s.id}`}>
              <div><p className="font-semibold">{s.product_name} · {s.plan_name}</p><p className="text-sm text-slate-500">{MODE_LABEL[s.interval]} · {money(s.amount, s.currency)} · renueva {fdate(s.current_period_end)}</p></div>
              <div className="flex items-center gap-3"><Badge s={s.status} />
                {s.cancel_at_period_end ? <span className="text-xs text-amber-700">Cancelación programada</span>
                  : ["active", "trialing"].includes(s.status) && <button onClick={() => cancel(s.id)} className="text-sm text-red-600 hover:underline" data-testid={`cancel-sub-${s.id}`}>Cancelar</button>}
              </div>
            </div>)) : <Empty text="No tiene suscripciones." />}
        </TabsContent>
        <TabsContent value="invoices" className="mt-6">
          {d.orders.length ? (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm"><thead className="bg-slate-50 text-left text-slate-500"><tr><th className="p-3">Pedido</th><th>Fecha</th><th>Total</th><th>Estado</th></tr></thead>
                <tbody>{d.orders.map((o) => <tr key={o.id} className="border-t border-slate-100" data-testid={`order-row-${o.number}`}><td className="p-3 font-mono">{o.number}</td><td>{fdate(o.created_at)}</td><td>{money(o.total, o.currency)}</td><td><Badge s={o.status} /></td></tr>)}</tbody></table>
              <p className="p-3 text-xs text-slate-500">Los comprobantes electrónicos oficiales (Hacienda CR) se integrarán con el proveedor de facturación electrónica.</p>
            </div>) : <Empty text="No hay facturas ni pagos registrados." />}
        </TabsContent>
        <TabsContent value="requests" className="mt-6 space-y-3">
          {d.requests.length ? d.requests.map((r) => <div key={r.id} className="card-cc !p-5 text-sm"><div className="flex items-center justify-between gap-3"><p className="font-semibold">{r.product ? `Demo · ${r.product}` : `${r.type === "soporte" ? "Soporte" : "Contacto"}${r.interest ? ` · ${r.interest}` : ""}`}</p><Badge s={r.status} /></div><p className="mt-1 text-slate-600 line-clamp-2">{r.message || r.comments}</p><p className="mt-1 text-xs text-slate-400">{fdate(r.created_at)}</p></div>) : <Empty text="No tiene solicitudes comerciales." />}
        </TabsContent>
        <TabsContent value="downloads" className="mt-6"><Empty text="Las descargas autorizadas (instaladores, manuales) se habilitarán por producto contratado." /></TabsContent>
        <TabsContent value="support" className="mt-6"><Support /></TabsContent>
        <TabsContent value="profile" className="mt-6"><Profile p={d.profile} reload={load} /></TabsContent>
      </Tabs>
    </section>
  );
}
